import type React from 'react';
import { useEffect, useMemo } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleRegular } from '@fluentui/react-icons';

const apiRelayPath = '/api/auth/admin-consent/notify';
const relayStatusParameterName = 'relay';
const relayCompleteParameterValue = 'complete';

const useStyles = makeStyles({
  container: {
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    height: '100dvh',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXL,
    textAlign: 'center',
    '@media (max-width: 768px)': {
      padding: tokens.spacingHorizontalL,
    },
  },
  icon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '64px',
    '@media (max-width: 768px)': {
      fontSize: '48px',
    },
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    '@media (max-width: 768px)': {
      fontSize: tokens.fontSizeBase500,
    },
  },
  message: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    margin: 0,
    maxWidth: '480px',
  },
});

export const AdminConsentRedirectPage: React.FC = () => {
  const styles = useStyles();
  const relayUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    const parameters = new URLSearchParams(window.location.search);
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;

    if (hash.includes('=')) {
      const hashParameters = new URLSearchParams(hash);
      hashParameters.forEach((value, key) => parameters.append(key, value));
    }

    if (parameters.get(relayStatusParameterName) === relayCompleteParameterValue) {
      return null;
    }

    parameters.delete(relayStatusParameterName);

    const queryString = parameters.toString();
    return queryString ? `${apiRelayPath}?${queryString}` : null;
  }, []);

  useEffect(() => {
    if (!relayUrl) {
      return;
    }

    void fetch(relayUrl, {
      cache: 'no-store',
      credentials: 'omit',
      mode: 'no-cors',
      method: 'POST',
    }).catch(() => undefined);
  }, [relayUrl]);

  return (
    <div className={styles.container}>
      <CheckmarkCircleRegular className={styles.icon} />
      <h1 className={styles.title}>Admin consent received</h1>
      <p className={styles.message}>
        Thank you. The admin consent response has been received and is being processed.
      </p>
    </div>
  );
};